import { useMemo } from "react";
import { shallowEqual } from "react-redux";
import { useGradeSelectorSelector } from "../redux/hooks";
import { selectModel } from "../redux/slices/GradeSelectorModelSlice";
import type { GradeBodyTypeType } from "./types/Model";
import { applyFilters, applyLcvFilters, hasActiveFilters as checkActiveFilters, mergeBodyTypes } from "./filterUtils";
import useLcvFilters from "../hooks/useLcvFilters";
import type {
    ApiGradeBodyEngineTransmissionWheeldriveCarType,
    SBRockarCarType,
} from "../../../../common-deprecated/types/ApiModel";

export const useFilteredGradeBodyTypes = (): GradeBodyTypeType[] => {
    const activeFilters = useGradeSelectorSelector((state) => state.gradeSelectorFilters.filters);
    const lcvFilters = useLcvFilters();

    const hasActiveFilters = checkActiveFilters(activeFilters);

    const gradeBodyTypes = useGradeSelectorSelector((state) => selectModel(state).gradeBodyTypes, shallowEqual);

    return useMemo(() => {
        if (lcvFilters && lcvFilters.hasActiveFilters) {
            return applyLcvFilters(gradeBodyTypes, lcvFilters.filteredCars);
        } else {
            if (!hasActiveFilters) {
                return gradeBodyTypes;
            }

            return applyFilters(activeFilters, gradeBodyTypes);
        }
    }, [lcvFilters, gradeBodyTypes, hasActiveFilters, activeFilters]);
};

export const useFilterCarExtrasAndPacks = (
    car: ApiGradeBodyEngineTransmissionWheeldriveCarType | SBRockarCarType,
): { extras: string[]; packs: string[] } => {
    const activeFilters = useGradeSelectorSelector((state) => state.gradeSelectorLcvFilters.activeFilters);
    const filteredCars = useLcvFilters()?.filteredCars;

    return useMemo(() => {
        if (!filteredCars) return { extras: [], packs: [] };

        const filteredCar = filteredCars?.find(
            (filtered) =>
                filtered.id === (car as ApiGradeBodyEngineTransmissionWheeldriveCarType).CarID ||
                filtered.id === (car as SBRockarCarType).vehicle?.base?.externalId,
        );

        if (!filteredCar) return { extras: [], packs: [] };

        const extras = filteredCar.filterValues
            .map((filterValue) => filterValue.extras)
            .flat()
            .filter(Boolean);

        const packs = filteredCar.filterValues
            .map((filterValue) => filterValue.packs || [])
            .flat()
            .filter(Boolean);

        // Set is used to remove duplicates
        return {
            extras: [...new Set(extras)],
            packs: [...new Set(packs)],
        };
    }, [filteredCars, car, activeFilters]);
};

// Retailer variant of useFilteredGradeBodyTypes, additional sorting is needed here.
export const useRetailerFilteredGradeBodyTypes = (hasFilter: boolean): GradeBodyTypeType[] => {
    const allGradeBodyTypes = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.gradeBodyTypes);
    const hasLcvFilters = useGradeSelectorSelector(
        (state) => state.gradeSelectorLcvFilters.initialized && state.gradeSelectorLcvFilters.filters.length > 0,
    );
    const filteredGradeBodyTypes = useFilteredGradeBodyTypes();

    // Important useMemo to reduce amount of rerenders.
    const sortedBodyTypes = useMemo(() => {
        return mergeBodyTypes(allGradeBodyTypes);
    }, [allGradeBodyTypes]);

    return hasFilter || hasLcvFilters ? filteredGradeBodyTypes : sortedBodyTypes;
};
